import { useEffect, useState } from "react";
import api from "../lib/api";
import ImageUpload from "../components/ImageUpload";
import { toast } from "sonner";
import { Trash2, Edit3, X, Calendar, Users } from "lucide-react";

const empty = {
  name: "", description: "", date: "", start: "19:00", end: "23:00",
  capacity: 60, image_url: "", is_paid: false, price: "",
  mode: "parallel", is_published: true,
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/events/all").then((r) => setEvents(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, capacity: Number(form.capacity) };
    try {
      if (editing) await api.patch(`/events/${editing}`, payload);
      else await api.post("/events", payload);
      toast.success("Event saved");
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error("Failed to save"); }
  };

  const startEdit = (ev) => {
    setEditing(ev.id);
    setForm({
      name: ev.name, description: ev.description || "", date: ev.date,
      start: ev.start, end: ev.end, capacity: ev.capacity,
      image_url: ev.image_url || "", is_paid: !!ev.is_paid, price: ev.price || "",
      mode: ev.mode || "parallel", is_published: ev.is_published !== false,
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event and its bookings?")) return;
    await api.delete(`/events/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="font-serif-display text-4xl text-[#5979A9]">Events</h1>
          <p className="text-[#4A5568] text-sm mt-1">Special nights, live music, supper clubs — independent capacity from restaurant.</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }} data-testid="new-event-button" className="btn-primary !py-2.5 !px-5 !text-sm">+ New Event</button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`event-row-${ev.id}`} className="bg-white rounded-2xl border border-[#F0EAE6] overflow-hidden">
            {ev.image_url && <img src={ev.image_url} alt={ev.name} className="w-full h-40 object-cover"/>}
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-serif-display text-xl text-[#5979A9]">{ev.name}</h3>
                <span className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full ${
                  ev.is_published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                }`}>{ev.is_published ? "Published" : "Draft"}</span>
              </div>
              <p className="text-sm text-[#4A5568] line-clamp-2 mb-3">{ev.description}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#4A5568] mb-3">
                <span className="inline-flex items-center gap-1"><Calendar size={12}/> {ev.date} · {ev.start}–{ev.end}</span>
                <span className="inline-flex items-center gap-1"><Users size={12}/> {ev.booked_count}/{ev.capacity}</span>
                <span className={`px-2 py-0.5 rounded-full font-semibold ${ev.mode === "close_restaurant" ? "bg-[#E05947]/10 text-[#E05947]" : "bg-[#5979A9]/10 text-[#5979A9]"}`}>
                  {ev.mode === "close_restaurant" ? "Restaurant closed" : "Parallel"}
                </span>
                {ev.is_paid && <span className="px-2 py-0.5 rounded-full bg-[#5979A9]/10 text-[#5979A9] font-semibold">{ev.price || "Paid"}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => startEdit(ev)} className="text-xs px-3 py-1.5 rounded-full bg-[#FAF4F1] hover:bg-[#F0EAE6] flex items-center gap-1"><Edit3 size={12}/>Edit</button>
                <button onClick={() => remove(ev.id)} className="text-xs px-3 py-1.5 rounded-full text-[#E05947] flex items-center gap-1"><Trash2 size={12}/>Delete</button>
              </div>
            </div>
          </div>
        ))}
        {events.length === 0 && <p className="text-[#4A5568] col-span-full">No events yet — click "New Event" to schedule one.</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-3xl max-w-lg w-full p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif-display text-2xl text-[#5979A9]">{editing ? "Edit Event" : "New Event"}</h2>
              <button onClick={() => setShowForm(false)}><X size={20}/></button>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <F label="Event name" value={form.name} onChange={(v) => setForm({...form, name: v})} required testid="event-name-input"/>
              <F label="Description" textarea value={form.description} onChange={(v) => setForm({...form, description: v})}/>
              <div className="grid grid-cols-3 gap-3">
                <F label="Date" type="date" value={form.date} onChange={(v) => setForm({...form, date: v})} required testid="event-date-input"/>
                <F label="Start" type="time" value={form.start} onChange={(v) => setForm({...form, start: v})} required/>
                <F label="End" type="time" value={form.end} onChange={(v) => setForm({...form, end: v})} required/>
              </div>
              <F label="Max capacity" type="number" min="1" value={form.capacity} onChange={(v) => setForm({...form, capacity: v})} required testid="event-capacity-input"/>
              <ImageUpload value={form.image_url} onChange={(v) => setForm({...form, image_url: v})} testid="event-image"/>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm pt-2">
                  <input type="checkbox" checked={form.is_paid} onChange={(e) => setForm({...form, is_paid: e.target.checked})} className="accent-[#E05947]"/>
                  Paid event
                </label>
                {form.is_paid && <F label="Price label" value={form.price} onChange={(v) => setForm({...form, price: v})} placeholder="€25 / person"/>}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Restaurant booking mode</label>
                <select value={form.mode} onChange={(e) => setForm({...form, mode: e.target.value})} data-testid="event-mode-select"
                        className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2">
                  <option value="parallel">Parallel — keep restaurant reservations open</option>
                  <option value="close_restaurant">Close restaurant during event time</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({...form, is_published: e.target.checked})} className="accent-[#E05947]"/>
                Published (visible to customers)
              </label>
              <button type="submit" data-testid="event-save-button" className="btn-primary w-full">Save Event</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, textarea, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
                  data-testid={testid}
                  className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2 resize-none" {...rest}/>
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)}
               data-testid={testid}
               className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2" {...rest}/>
      )}
    </div>
  );
}
